import { dehydrate } from '@tanstack/react-query';
import { getQueryClient, isMobileRequest, useMobile } from 'common-nextjs';
import { createSession } from 'fetcher-session';
import React, { useEffect, useState } from 'react';
import { LegacyButtonLink } from 'ui';
import { mapValues, qsStringify, qsUpdateString } from 'utils';
import { fetchAllPageViewData, fetchPageView } from '~/api/tiles';
import HowItWorksShippingRow from '~/components/HowItWorks/HowItWorksShippingRow';
import Image from '~/components/Image';
import Layout from '~/components/Layout';
import EventsLink from '~/components/Links/EventsLink';
import { playStoreLink } from '~/components/Links/GooglePlayStoreLink';
import { appStoreLink } from '~/components/Links/IosAppStoreLink';
import ListFlowLink from '~/components/Links/ListFlowLink';
import NewTabLink from '~/components/Links/NewTabLink';
import SellNowPredictive from '~/components/SellNowPredictive';
import PageView from '~/components/legos/PageView';
import { PageViewDataContext } from '~/contexts/PageViewDataContext';
import DESKTOP_CTA from '~/public/static/sell-now/desktop-app-cta.png';
import MOBILE_CTA_ANDROID from '~/public/static/sell-now/mobile-app-cta-android.png';
import MOBILE_CTA_IOS from '~/public/static/sell-now/mobile-app-cta-ios.png';
import ArrowRightIcon from '~/public/static/svg/ArrowRightIcon';
import {
  clickedAppCta,
  visitedSellNow,
} from '~/services/analytics/events/generic';
import { CustomNextPage } from '~/typings/custom-next';
import { RailsPredictiveSearchResult } from '~/typings/services/rails/predictiveSearch';
import { RailsPageView } from '~/typings/services/rails/tile';

interface Props {
  pageView?: RailsPageView;
  pageViewData?: any;
}

const SellNowPage: CustomNextPage<Props> = ({ pageView, pageViewData }) => {
  const mobile = useMobile();

  const appCtaLink = mobile
    ? typeof window !== 'undefined' &&
      (window.navigator.platform.startsWith('i') ||
        window.navigator.platform.startsWith('Mac') ||
        window.navigator.platform.startsWith('Mac'))
      ? appStoreLink
      : playStoreLink
    : appStoreLink;

  const appCtaUrl = mobile
    ? typeof window !== 'undefined' &&
      (window.navigator.platform.startsWith('i') ||
        window.navigator.platform.startsWith('Mac') ||
        window.navigator.platform.startsWith('Mac'))
      ? MOBILE_CTA_IOS
      : MOBILE_CTA_ANDROID
    : DESKTOP_CTA;

  useEffect(() => visitedSellNow(), []);

  return (
    <Layout
      title="Sell on SidelineSwap"
      container={false}
      excludeAppCta
      excludeMobileSearch
    >
      <section className="mb-4 md:mb-6">
        <div className="container mx-auto mb-0 py-12 md:py-28">
          <h1 className="mb-6 text-center text-4xl font-bold">
            What are you selling?
          </h1>

          <PredictiveSearchField />

          <div className="mt-6 flex justify-center md:mt-10">
            <EventsLink className="flex items-center space-x-2 px-4 py-3 font-semibold text-green-500">
              <span>Or Find a Trade-in Event</span>
              <ArrowRightIcon fill="currentColor" className="h-4 w-4" />
            </EventsLink>
          </div>
        </div>
      </section>
      <section className="container mb-4">
        <h1 className="mb-6 text-center text-2xl">List. Ship. Get Paid.</h1>

        <HowItWorksShippingRow />

        <div className="mt-6 flex justify-center">
          <LegacyButtonLink
            as={<ListFlowLink src="sell-now" />}
            color="primary"
            style={{ width: 208 }}
          >
            Sell Now
          </LegacyButtonLink>
        </div>
      </section>
      <PageViewDataContext value={{ data: pageViewData, pageView }}>
        <PageView pageView={pageView} />
      </PageViewDataContext>
      <NewTabLink
        href={appCtaLink}
        title="Download the app"
        className="block px-4 pt-4"
        css={`
          height: 300px;
          background: hsl(184, 51%, 50%);
          background: linear-gradient(
            90deg,
            hsl(184, 51%, 50%) 0%,
            hsl(232, 55%, 8%) 100%
          );
        `}
        onClick={() => clickedAppCta(undefined, 'sell-now')}
      >
        <Image
          alt="Download the app"
          src={appCtaUrl}
          css={`
            display: block;
            height: 100%;
            margin: auto;
          `}
        />
      </NewTabLink>
    </Layout>
  );
};

const PredictiveSearchField: React.FC = () => {
  const [value, setValue] = useState<RailsPredictiveSearchResult | null>(null);
  return (
    <div
      className="mx-auto"
      style={{
        maxWidth: 600,
      }}
    >
      <SellNowPredictive
        onSelect={(result, searchText) => {
          setValue(result);
          if (result) {
            window.location.assign(
              `/items/new${qsUpdateString([
                mapValues(result.query!, key => result.query?.[key]?.[0]),
                {
                  src: 'sell-now',
                  prefill_data: 1,
                },
              ])}`,
            );
          } else {
            window.location.assign(
              `/items/new${qsStringify({
                src: 'sell-now',
                title: searchText,
              })}`,
            );
          }
        }}
        value={value}
      />

      <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-3">
        <InstructionCell
          index={1}
          description="Type a keyword that describes your item"
        />
        <InstructionCell index={2} description="Choose from the suggestions" />
        <InstructionCell
          index={3}
          description={'Or just tap "Start Listing" to start from scratch'}
        />
      </div>
    </div>
  );
};

interface InstructionCellProps {
  index: number;
  description: string;
}

const InstructionCell: React.FC<InstructionCellProps> = ({
  index,
  description,
}) => (
  <div className="bg-mint-green-500 rounded px-4 py-3 text-center">
    <div className="mb-2 flex justify-center">
      <div className="rounded-full bg-white px-2 py-0.5 font-semibold">
        {index}
      </div>
    </div>

    <div className="text-base font-semibold">{description}</div>
  </div>
);

SellNowPage.getInitialProps = async ctx => {
  const queryClient = getQueryClient();
  try {
    const pageView = await fetchPageView.call(ctx, 693);
    const pageViewData = await fetchAllPageViewData(
      pageView,
      isMobileRequest(ctx),
      undefined,
      queryClient,
      createSession(ctx),
    );

    return {
      pageView,
      pageViewData,
    };
  } catch (e) {}

  return {
    dehydratedState: dehydrate(queryClient),
  };
};

export default SellNowPage;
